import {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import throttle from "lodash.throttle";
import styles from "./Cursor.module.scss";
import playCursorImg from "../../assets/images/play-cursor.png";
import pauseCursorImg from "../../assets/images/pause-cursor.png";
import defaultCursorImg from "../../assets/images/default-cursor.png";
import pointerCursorImg from "../../assets/images/pointer-cursor.png";
import textCursorImg from "../../assets/images/text-cursor.png";
import isMobilePhone from "../../helpers/isMobilePhone.helper";

export enum CURSOR_ENUM {
  PLAY = "PLAY",
  PAUSE = "PAUSE",
  DEFAULT = "DEFAULT",
  POINTER = "POINTER",
  TEXT = "TEXT",
}

export const CURSOR_IMAGES = {
  PLAY: playCursorImg,
  PAUSE: pauseCursorImg,
  DEFAULT: defaultCursorImg,
  POINTER: pointerCursorImg,
  TEXT: textCursorImg,
};

interface CursorProps {
  delay?: number;
  cursor: CURSOR_ENUM;
}

interface CursorOptions {
  _x: number;
  _y: number;
  endX: number;
  endY: number;
  cursorVisible: boolean;
  cursorEnlarged: boolean;
  outlineSize?: number;
}

const Cursor = ({ delay = 5, cursor = CURSOR_ENUM.DEFAULT }: CursorProps) => {
  const dotOutline: RefObject<HTMLImageElement> = useRef(null);
  const animationFrame: any = useRef(null);

  const cursorOptions: CursorOptions = useMemo(
    () => ({
      _x: 0,
      _y: 0,
      endX: window.innerWidth / 2,
      endY: window.innerHeight / 2,
      cursorVisible: true,
      cursorEnlarged: false,
    }),
    []
  );

  const toggleCursorSize = useCallback(() => {
    if (dotOutline?.current && cursor !== CURSOR_ENUM.POINTER) {
      if (cursorOptions.cursorEnlarged) {
        dotOutline.current.style.transform = "translate(-50%, -50%) scale(1.5)";
      } else {
        dotOutline.current.style.transform = "translate(-50%, -50%) scale(1)";
      }
    }
  }, [cursorOptions, cursor]);

  const toggleCursorVisibility = useCallback(
    throttle(() => {
      if (dotOutline?.current?.style) {
        if (cursorOptions.cursorVisible) {
          dotOutline.current.style.opacity = "1";
        } else {
          dotOutline.current.style.opacity = "0";
        }
      }
    }, 200),
    [cursorOptions]
  );

  // const onMouseOver = useCallback(() => {
  //   cursor.cursorEnlarged = true;
  //   toggleCursorVisibility();
  // }, [cursor, toggleCursorVisibility]);

  // const onMouseOut = useCallback(() => {
  //   cursor.cursorEnlarged = false;
  //   toggleCursorVisibility();
  // }, [cursor, toggleCursorVisibility]);

  const onMouseDown = useCallback(() => {
    cursorOptions.cursorEnlarged = true;
    toggleCursorSize();
  }, [cursorOptions, toggleCursorSize]);

  const onMouseUp = useCallback(() => {
    cursorOptions.cursorEnlarged = false;
    toggleCursorSize();
  }, [cursorOptions, toggleCursorSize]);

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      cursorOptions.cursorVisible = true;
      toggleCursorVisibility();

      cursorOptions.endX = event.pageX;
      cursorOptions.endY = event.pageY;
    },
    [cursorOptions, toggleCursorVisibility]
  );

  // const onTouchMove = useCallback(
  //   (event: TouchEvent) => {
  //     cursor.cursorVisible = true;
  //     toggleCursorVisibility();

  //     cursor.endX = event.changedTouches[0].pageX;
  //     cursor.endY = event.changedTouches[0].pageY;
  //   },
  //   [cursor, toggleCursorVisibility]
  // );

  const onMouseEnter = useCallback(() => {
    cursorOptions.cursorVisible = true;
    toggleCursorVisibility();
    if (dotOutline?.current?.style) {
      dotOutline.current.style.opacity = "1";
    }
  }, [cursorOptions, toggleCursorVisibility]);

  const onMouseLeave = useCallback(() => {
    cursorOptions.cursorVisible = false;
    toggleCursorVisibility();
    if (dotOutline?.current?.style) {
      dotOutline.current.style.opacity = "0";
    }
  }, [cursorOptions, toggleCursorVisibility]);

  const addEventListeners = useCallback(() => {
    document.addEventListener("mousedown", onMouseDown, false);
    document.addEventListener("mouseup", onMouseUp, false);
    document.addEventListener("mousemove", onMouseMove, false);
    // document.addEventListener("touchmove", onTouchMove, false);
    // Hide/show cursor
    document.addEventListener("mouseenter", onMouseEnter, false);
    document.addEventListener("mouseleave", onMouseLeave, false);
  }, [onMouseDown, onMouseUp, onMouseMove, onMouseEnter, onMouseLeave]);

  const removeEventListeners = useCallback(() => {
    document.removeEventListener("mousedown", onMouseDown);
    document.removeEventListener("mouseup", onMouseUp);
    document.removeEventListener("mousemove", onMouseMove);
    // document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("mouseenter", onMouseEnter);
    document.removeEventListener("mouseleave", onMouseLeave);
  }, [onMouseDown, onMouseUp, onMouseMove, onMouseEnter, onMouseLeave]);

  const animateDotOutline = useCallback(() => {
    cursorOptions._x += (cursorOptions.endX - cursorOptions._x) / delay;
    cursorOptions._y += (cursorOptions.endY - cursorOptions._y) / delay;
    if (dotOutline?.current?.style) {
      dotOutline.current.style.top = cursorOptions._y + "px";
      dotOutline.current.style.left = cursorOptions._x + "px";
    }

    animationFrame.current = requestAnimationFrame(animateDotOutline);
  }, [delay, cursorOptions, dotOutline, animationFrame]);

  const initCursor = useCallback(() => {
    animateDotOutline();
  }, [animateDotOutline]);

  const preloadImages = useCallback(() => {
    Object.values(CURSOR_IMAGES).forEach((fileName) => {
      const img = new Image();
      img.src = fileName;
    });
  }, []);

  useEffect(() => {
    if (isMobilePhone() || !dotOutline?.current || !cursor) {
      return;
    }

    initCursor();
    addEventListeners();
    preloadImages();

    return () => {
      removeEventListeners();
      cancelAnimationFrame(animationFrame);
    };
  }, [
    cursor,
    initCursor,
    preloadImages,
    animationFrame,
    addEventListeners,
    removeEventListeners,
  ]);

  return (
    <>
      {cursor && (
        <img
          alt="cursor-outline"
          draggable={false}
          src={CURSOR_IMAGES[cursor]}
          ref={dotOutline}
          className={`${styles["cursor-dot-outline"]} ${
            styles[`cursor-dot-outline--${cursor}`]
          }`}
        />
      )}
    </>
  );
};

const areEqual = (
  prevProps: Readonly<CursorProps>,
  nextProps: Readonly<CursorProps>
) => prevProps.cursor === nextProps.cursor;

export default memo(Cursor, areEqual);
